import { Assembly, PointOfInterest } from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useFeatureFlag } from '@/hooks';
import { primary } from '@/theme/colors';
import { Stack, Tooltip, Typography } from '@mui/material';
import { usePoiTemplateFields } from '../../hooks/usePoiTemplateFields';
import { ActionMenu } from './ActionMenu';
import { Preview } from './Preview';
import { ReviewAction } from './ReviewAction';
import { ReviewStatus } from './ReviewStatus';
import { Card, Details, Field, Header, StyledCheckbox, Tile } from './styles';
import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state/atoms';
import { useMemo } from 'react';
import { LocationTag } from '../../../../../constants';

interface SummaryCardProps {
  data: PointOfInterest;
  poiIndex?: number;
  variant?: 'default' | 'compact';
  onClick?: (poiId: string | undefined, assembly: Assembly | undefined) => void;
  poiType?: string;
}

export const SummaryCard = ({
  data,
  poiIndex,
  variant = 'default',
  onClick,
  poiType,
}: SummaryCardProps) => {
  const [selectedReviewPointsOfInterest, setSelectedReviewPointsOfInterest] = useAtom(
    state.selectedReviewPointsOfInterest
  );
  const isAllReviewPointsOfInterestSelected = useAtomValue(
    state.isAllReviewPointsOfInterestSelected
  );
  const selectedReviewPoiTypes = useAtomValue(state.selectedReviewPoiTypes);
  const { templateFieldsWithValues, templateOfSelectedPoi } = usePoiTemplateFields({ data });
  const isAITEngineer = useFeatureFlag('ait-engineer');
  const isAPIInspector = useFeatureFlag('api-inspector');

  const poiName = generatePoiDisplayName({
    name: data.name,
    poiId: data.poiId,
    poiType: data.type,
  });

  const isPoiTypeSelected = useMemo(
    () => selectedReviewPoiTypes.includes(poiType || ''),
    [selectedReviewPoiTypes, poiType]
  );

  const isPoiSelected = useMemo(
    () =>
      isPoiTypeSelected ||
      isAllReviewPointsOfInterestSelected ||
      selectedReviewPointsOfInterest.includes(data.id || ''),
    [
      isPoiTypeSelected,
      isAllReviewPointsOfInterestSelected,
      selectedReviewPointsOfInterest,
      data.id,
    ]
  );

  const handleCheckboxChange = () => {
    if (isPoiSelected) {
      setSelectedReviewPointsOfInterest((prev) => prev.filter((id) => id !== data.id));
    } else {
      setSelectedReviewPointsOfInterest((prev) => [...prev, data.id]);
    }
  };

  const variantLocation = useMemo(
    () => (variant === 'compact' ? LocationTag.ViewerPoiList : LocationTag.ReviewPanelPoiList),
    [variant]
  );

  if (variant === 'compact') {
    return (
      <Tile
        data-testid="summary-tile-component"
        onClick={() => onClick?.(data.id, data.assembly)}
        id="viewer-poi-list-tile"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap="0.8rem"
          sx={{
            flex: 1,
            maxWidth: '50%',
          }}
        >
          <StyledCheckbox
            disableRipple
            checked={isPoiSelected}
            onChange={handleCheckboxChange}
            onClick={(event) => event.stopPropagation()}
            checkboxSize="sm"
            sx={{ margin: 0 }}
          />
          <Tooltip
            title={poiName}
            placement="left"
            arrow
            disableHoverListener={poiName.length < 20}
          >
            <Typography
              variant="caption"
              color="primary.500"
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 'fit-content',
              }}
            >
              {poiName}
            </Typography>
          </Tooltip>
          {data.isGoverning && (
            <Tooltip title="Governing POI" arrow placement="top">
              <span>
                <AbyssIcon iconName="governing-outline" size="md" />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" gap="1.2rem" alignItems="center">
          <Stack direction="column" gap="1.2rem" alignItems="flex-end">
            {isAITEngineer && (
              <ReviewStatus
                pointOfInterest={data}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={variantLocation}
              />
            )}
            {isAPIInspector && (
              <ReviewAction
                pointOfInterest={data}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={variantLocation}
              />
            )}
          </Stack>
          <ActionMenu pointOfInterest={data} location={variantLocation} />
        </Stack>
      </Tile>
    );
  }

  return (
    <Card selected={isPoiSelected} id="review-panel-poi-list-card">
      <div data-testid="preview-component">
        <Preview data={data} poiIndex={poiIndex} />
      </div>
      <Stack flexGrow="1" gap="0.8rem">
        <Header>
          <Stack
            direction="row"
            alignItems="flex-start"
            gap="1.2rem"
            sx={{
              width: '70%',
              overflow: 'hidden',
              flexGrow: 1,
            }}
          >
            <StyledCheckbox
              disableRipple
              checked={isPoiSelected}
              onChange={handleCheckboxChange}
              onClick={(event) => event.stopPropagation()}
              checkboxSize="md"
            />
            <Stack direction="row" alignItems="center" gap={1}>
              <Tooltip
                title={poiName}
                placement="left"
                arrow
                disableInteractive={poiName.length < 20}
              >
                <Typography
                  variant="h5"
                  color="primary"
                  fontWeight={600}
                  noWrap
                  sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                >
                  {poiName}
                </Typography>
              </Tooltip>

              {data.createdByUser?.roleIds?.some((role) => role?.name === 'api-inspector') ? (
                <UserAvatar
                  name={data.createdByUser?.name}
                  picture={data.createdByUser?.picture}
                  sx={{ width: '2.5rem', height: '2.5rem' }}
                />
              ) : (
                <UserAvatar
                  picture="/assets/abyss_fabric_mini_logo_white.svg"
                  sx={{ width: '2.5rem', height: '2.5rem', background: '#000', padding: '0.25rem' }}
                />
              )}
            </Stack>
            {data.isGoverning && (
              <Tooltip title="Governing POI" arrow placement="top">
                <span>
                  <AbyssIcon iconName="governing-outline" />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" gap="1.2rem" alignItems="center">
            {isAITEngineer && (
              <ReviewStatus
                pointOfInterest={data}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={variantLocation}
              />
            )}
            {isAPIInspector && (
              <ReviewAction
                pointOfInterest={data}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={variantLocation}
              />
            )}
            <ActionMenu pointOfInterest={data} location={variantLocation} />
          </Stack>
        </Header>
        <Details
          data-testid="details-component"
          columnCount={templateFieldsWithValues.length > 4 ? 2 : 1}
        >
          {templateFieldsWithValues.map((field) => (
            <Field key={field.label}>
              <Typography lineHeight="2.4rem" variant="h6" fontWeight={400} color={primary.gray}>
                {field.label}:
              </Typography>
              <Typography lineHeight="2.4rem" variant="h6" color={primary.independence}>
                {field.value}
              </Typography>
            </Field>
          ))}
        </Details>
      </Stack>
    </Card>
  );
};
